/**
 * Accordion Component Definition
 *
 * Expandable accordion with collapsible items.
 */

export const accordionComponent = {
  type: 'accordion',
  label: 'Accordion',
  icon: 'ChevronDown',
  description: 'Expandable accordion with collapsible items',
  category: 'content',

  canHaveChildren: false,

  resolveStyles() {
    return {};
  },

  defaultProps: {
    // Content
    items: [
      { id: '1', title: 'Accordion Item 1', content: 'Content for the first accordion item goes here.', defaultOpen: true },
      { id: '2', title: 'Accordion Item 2', content: 'Content for the second accordion item goes here.', defaultOpen: false },
    ],
    allowMultiple: false,
    iconPosition: 'right',
    iconType: 'chevron',

    // Header styling
    headerBackgroundColor: '#f9fafb',
    headerTextColor: '#1f2937',
    headerFontFamily: 'System Default',
    headerFontSize: 16,
    headerFontWeight: '600',
    headerPadding: { base: { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' }, tablet: null, desktop: null },

    // Content styling
    contentBackgroundColor: '#ffffff',
    contentTextColor: '#4b5563',
    contentFontFamily: 'System Default',
    contentFontSize: 14,
    contentPadding: { base: { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' }, tablet: null, desktop: null },

    // Container styling
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#e5e7eb',
    borderRadius: 8,
    itemGap: 0,

    // Spacing
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Advanced
    visibility: { base: true, tablet: null, desktop: null },

    // Positioning
    position: 'static',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Content category
    {
      key: 'items',
      type: 'accordionItems',
      label: 'Accordion Items',
      category: 'Content',
    },
    {
      key: 'allowMultiple',
      type: 'checkbox',
      label: 'Allow Multiple Open',
      category: 'Content',
      default: false,
    },
    {
      key: 'iconPosition',
      type: 'select',
      label: 'Icon Position',
      category: 'Content',
      default: 'right',
      options: [
        { value: 'left', label: 'Left' },
        { value: 'right', label: 'Right' },
      ],
    },
    {
      key: 'iconType',
      type: 'select',
      label: 'Icon Type',
      category: 'Content',
      default: 'chevron',
      options: [
        { value: 'chevron', label: 'Chevron' },
        { value: 'plus-minus', label: 'Plus/Minus' },
        { value: 'arrow', label: 'Arrow' },
        { value: 'none', label: 'None' },
      ],
    },

    // Header Style category
    {
      key: 'headerBackgroundColor',
      type: 'color',
      label: 'Header Background',
      category: 'Header Style',
      default: '#f9fafb',
    },
    {
      key: 'headerTextColor',
      type: 'color',
      label: 'Header Text Color',
      category: 'Header Style',
      default: '#1f2937',
    },
    {
      key: 'headerFontFamily',
      type: 'fontFamily',
      label: 'Header Font Family',
      category: 'Header Style',
      default: 'System Default',
    },
    {
      key: 'headerFontSize',
      type: 'number',
      label: 'Header Font Size (px)',
      category: 'Header Style',
      min: 10,
      max: 48,
      default: 16,
    },
    {
      key: 'headerFontWeight',
      type: 'select',
      label: 'Header Font Weight',
      category: 'Header Style',
      default: '600',
      options: [
        { value: 'normal', label: 'Normal' },
        { value: '500', label: 'Medium' },
        { value: '600', label: 'Semibold' },
        { value: 'bold', label: 'Bold' },
      ],
    },
    {
      key: 'headerPadding',
      type: 'responsivePadding',
      label: 'Header Padding',
      category: 'Header Style',
    },

    // Content Style category
    {
      key: 'contentBackgroundColor',
      type: 'color',
      label: 'Content Background',
      category: 'Content Style',
      default: '#ffffff',
    },
    {
      key: 'contentTextColor',
      type: 'color',
      label: 'Content Text Color',
      category: 'Content Style',
      default: '#4b5563',
    },
    {
      key: 'contentFontFamily',
      type: 'fontFamily',
      label: 'Content Font Family',
      category: 'Content Style',
      default: 'System Default',
    },
    {
      key: 'contentFontSize',
      type: 'number',
      label: 'Content Font Size (px)',
      category: 'Content Style',
      min: 10,
      max: 48,
      default: 14,
    },
    {
      key: 'contentPadding',
      type: 'responsivePadding',
      label: 'Content Padding',
      category: 'Content Style',
    },

    // Appearance category
    {
      key: 'backgroundColor',
      type: 'color',
      label: 'Background Color',
      category: 'Appearance',
      default: '#ffffff',
    },
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Appearance',
      min: 0,
      max: 10,
      default: 1,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Appearance',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Appearance',
      default: '#e5e7eb',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Appearance',
      min: 0,
      max: 50,
      default: 8,
    },
    {
      key: 'itemGap',
      type: 'number',
      label: 'Item Gap (px)',
      category: 'Appearance',
      min: 0,
      max: 32,
      default: 0,
    },

    // Spacing category
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Advanced category
    {
      key: 'visibility',
      type: 'responsiveVisibility',
      label: 'Visibility',
      category: 'Advanced',
      default: { base: true, tablet: null, desktop: null },
    },
    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'static',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },
  ],

  // Render function - will be implemented in the unified renderer
  render: null,
};

export default accordionComponent;
